import { type DependencyList, useCallback, useRef, useState } from 'react';
import { useMountedState } from './useMountedState';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as the built-in ReturnType
type PromiseType<P extends Promise<any>> = P extends Promise<infer T> ? T : never;

export type AsyncState<T> =
    | {
          loading: boolean;
          error?: Error | undefined;
          value?: T;
      }
    | {
          loading: false;
          error: Error;
          value?: undefined;
      }
    | {
          loading: false;
          error?: undefined;
          value: T;
      };

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as the built-in ReturnType
type FunctionReturningPromise = (...args: any[]) => Promise<any>;

type StateFromFunctionReturningPromise<T extends FunctionReturningPromise> = AsyncState<PromiseType<ReturnType<T>>>;

type AsyncFnReturn<T extends FunctionReturningPromise = FunctionReturningPromise> = [
    StateFromFunctionReturningPromise<T>,
    T,
];

export function useAsyncFn<T extends FunctionReturningPromise>(
    fn: T,
    deps: DependencyList = [],
    initialState: StateFromFunctionReturningPromise<T> = { loading: false }
): AsyncFnReturn<T> {
    const lastCallId = useRef(0);
    const isMounted = useMountedState();
    const [state, setState] = useState<StateFromFunctionReturningPromise<T>>(initialState);
    const callback = useCallback(
        (...args: Parameters<T>): ReturnType<T> => {
            const callId = ++lastCallId.current;

            if (!state.loading) {
                setState((prevState) => {
                    return { ...prevState, loading: true };
                });
            }

            return fn(...args).then(
                (value) => {
                    isMounted() && callId === lastCallId.current && setState({ value, loading: false });

                    return value;
                },
                (error) => {
                    isMounted() && callId === lastCallId.current && setState({ error, loading: false });

                    return error;
                }
            ) as ReturnType<T>;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- flo
        deps
    );

    return [state, callback as unknown as T];
}
